import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "../../animations/gsap.config";
import { ScrollTrigger } from "../../animations/gsap.config";
import { NAVIGATION, SERVICES } from "../../constants/data";
import {
  Phone,
  Mail,
  MapPin,
  ArrowUpRight,
  Send,
  ChevronRight,
} from "lucide-react";
import {
  FaLinkedinIn,
  FaFacebookF,
  FaTwitter,
  FaSpinner,
} from "react-icons/fa";
import { useMutation } from "@tanstack/react-query";
import api from "../../api/api";
import toast from "react-hot-toast";

/* ─────────────────────────────────────────────
 ANIMATED LOGO MARK (reusable)
───────────────────────────────────────────── */
const FooterLogo = ({ logo }) => (
  <div className="flex items-center gap-3">
    <div className="relative w-40 h-40 rounded-2xl overflow-hidden flex-shrink-0">
      <img
        src={logo || COMPANY_INFO.logo}
        alt="Agora Consulting Logo"
        className="w-full h-full object-contain"
      />
    </div>
  </div>
);

/* ─────────────────────────────────────────────
 FOOTER LINK
───────────────────────────────────────────── */
const FooterLink = ({ to, children, external = false }) => {
  const ref = useRef(null);
  const arrowRef = useRef(null);

  const handleEnter = () => {
    gsap.to(ref.current, { x: 4, duration: 0.25, ease: "power2.out" });
    gsap.to(arrowRef.current, {
      opacity: 1,
      x: 0,
      duration: 0.25,
      ease: "power2.out",
    });
  };
  const handleLeave = () => {
    gsap.to(ref.current, { x: 0, duration: 0.25, ease: "power2.out" });
    gsap.to(arrowRef.current, { opacity: 0, x: -4, duration: 0.25 });
  };

  const classes = `flex items-center gap-2 text-sm text-slate-600
 hover:text-slate-900 transition-colors duration-300 group w-fit`;

  const content = (
    <>
      <ChevronRight size={12} className="text-primary-500 flex-shrink-0" />
      <span ref={ref}>{children}</span>
      <ArrowUpRight
        ref={arrowRef}
        size={11}
        className="text-secondary-500 flex-shrink-0"
        style={{ opacity: 0, transform: "translateX(-4px)" }}
      />
    </>
  );

  if (external) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noopener noreferrer"
        className={classes}
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      to={to}
      className={classes}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {content}
    </Link>
  );
};

/* ─────────────────────────────────────────────
 SOCIAL ICON BUTTON
───────────────────────────────────────────── */
const SocialBtn = ({ href, icon: Icon, label }) => {
  const ref = useRef(null);

  const handleEnter = () =>
    gsap.to(ref.current, {
      y: -3,
      scale: 1.1,
      duration: 0.25,
      ease: "back.out(2)",
    });
  const handleLeave = () =>
    gsap.to(ref.current, {
      y: 0,
      scale: 1,
      duration: 0.25,
      ease: "power2.out",
    });

  return (
    <a
      ref={ref}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="w-9 h-9 rounded-xl flex items-center justify-center
 border border-slate-300 text-slate-600
 hover:bg-primary-600 hover:border-primary-500 hover:text-white
 transition-colors duration-300"
    >
      <Icon size={14} />
    </a>
  );
};

/* ─────────────────────────────────────────────
 NEWSLETTER INPUT
───────────────────────────────────────────── */
const NewsletterForm = () => {
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const submitNewsLetterMutation = useMutation({
    mutationFn: (data) => {
      api.post("/api/newsletter/subscribe", { email: data });
    },
    onSuccess: () => {
      toast.success("Subscribed to NewsLetter!");
    },
    onError: (err) => {
      toast.error(
        `Submission failed: ${err?.response?.data?.message || err.message}`,
      );
    },
  });
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMessage("Please enter a valid email address");
      return;
    }
    submitNewsLetterMutation.mutate(email);

    // TODO: wire to API
  };

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-0 rounded-xl overflow-hidden
 border border-slate-300 focus-within:border-primary-500
 transition-colors duration-300 bg-white"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="flex-1 bg-transparent px-4 py-3 text-sm text-slate-900
 placeholder-slate-500 outline-none min-w-0"
          required
        />
        <button
          type="submit"
          disabled={submitNewsLetterMutation.isPending}
          className="flex-shrink-0 px-4 py-3 bg-primary-600 hover:bg-primary-500
 text-white transition-colors duration-300 flex items-center gap-2"
          aria-label="Subscribe"
        >
          {submitNewsLetterMutation.isPending ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <Send size={14} />
          )}
        </button>
      </form>
      <span className="text-sm text-red-500">
        {errorMessage && errorMessage}{" "}
      </span>
    </div>
  );
};

/* ─────────────────────────────────────────────
 BACK TO TOP BUTTON
───────────────────────────────────────────── */
const BackToTop = () => {
  const ref = useRef(null);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleEnter = () =>
    gsap.to(ref.current, { y: -3, duration: 0.25, ease: "power2.out" });
  const handleLeave = () =>
    gsap.to(ref.current, { y: 0, duration: 0.25, ease: "power2.out" });

  return (
    <button
      ref={ref}
      onClick={handleClick}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      aria-label="Back to top"
      className="w-10 h-10 rounded-xl border border-slate-300
 hover:border-primary-500 hover:bg-primary-50
 flex items-center justify-center text-slate-600
 hover:text-primary-600 transition-colors duration-300 flex-shrink-0"
    >
      <ArrowUpRight size={16} className="-rotate-45" />
    </button>
  );
};

/* ─────────────────────────────────────────────
 MAIN FOOTER
───────────────────────────────────────────── */
const Footer = ({ companyinfo }) => {
  const footerRef = useRef(null);
  const topLineRef = useRef(null);
  const columnsRef = useRef([]);
  const bottomBarRef = useRef(null);
  const glowRef = useRef(null);

  const year = new Date().getFullYear();

  /* ── Scroll-triggered reveal ── */
  useEffect(() => {
    const ctx = gsap.context(() => {
      /* Glow pulse */
      gsap.to(glowRef.current, {
        opacity: 0.6,
        scale: 1.1,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });

      /* Top divider line */
      gsap.fromTo(
        topLineRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: footerRef.current,
            start: "top 90%",
          },
        },
      );

      /* Columns stagger in */
      columnsRef.current.forEach((col, i) => {
        if (!col) return;
        gsap.fromTo(
          col,
          { y: 40, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            ease: "power3.out",
            delay: i * 0.1,
            scrollTrigger: {
              trigger: footerRef.current,
              start: "top 85%",
            },
          },
        );
      });

      /* Bottom bar */
      gsap.fromTo(
        bottomBarRef.current,
        { y: 20, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: "power3.out",
          scrollTrigger: {
            trigger: bottomBarRef.current,
            start: "top 98%",
          },
        },
      );
    }, footerRef);

    return () => ctx.revert();
  }, []);

  return (
    <footer ref={footerRef} className="relative overflow-hidden bg-slate-50">
      {/* Ambient glow */}
      <div
        ref={glowRef}
        className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2
 w-[600px] h-[300px] rounded-full pointer-events-none opacity-20"
        style={{
          background:
            "radial-gradient(ellipse, rgba(59,130,246,0.15) 0%, transparent 70%)",
        }}
      />

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 grid-pattern opacity-10 pointer-events-none" />

      {/* Top divider */}
      <div className="container-fluid">
        <div
          ref={topLineRef}
          className="h-px origin-left"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(59,130,246,0.3), rgba(245,158,11,0.3), transparent)",
            transform: "scaleX(0)",
          }}
        />
      </div>

      {/* ── MAIN CONTENT ── */}
      <div className="container-fluid py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-10 lg:gap-8">
          {/* ── Col 1: Brand ── */}
          <div
            ref={(el) => (columnsRef.current[0] = el)}
            className="sm:col-span-2 lg:col-span-1 xl:col-span-2 space-y-6"
          >
            <FooterLogo
              logo={`${process.env.REACT_APP_BACKEND_URL}${companyinfo?.logo}`}
            />

            <p className="text-sm text-slate-600 leading-relaxed max-w-xs">
              {companyinfo?.tagline}
            </p>

            {/* Social links */}
            <div className="flex items-center gap-2">
              <SocialBtn
                href={companyinfo?.linkedin}
                icon={FaLinkedinIn}
                label="LinkedIn"
              />
              <SocialBtn
                href={companyinfo?.twitter}
                icon={FaTwitter}
                label="Twitter"
              />
              <SocialBtn
                href={companyinfo?.facebook}
                icon={FaFacebookF}
                label="Facebook"
              />
            </div>

            {/* Newsletter */}
            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wider text-slate-700 uppercase">
                Stay Updated
              </p>
              <NewsletterForm />
            </div>
          </div>

          {/* ── Col 2: Quick Links ── */}
          <div ref={(el) => (columnsRef.current[1] = el)} className="space-y-5">
            <h4
              className="text-sm font-bold tracking-widest text-slate-900 uppercase
 flex items-center gap-2"
            >
              <span className="inline-block w-4 h-0.5 bg-secondary-500 rounded-full" />
              Navigation
            </h4>
            <ul className="space-y-3">
              {NAVIGATION.map((item) => (
                <li key={item.id}>
                  <FooterLink to={item.href}>{item.label}</FooterLink>
                </li>
              ))}
              <li>
                <FooterLink to="/careers">Careers</FooterLink>
              </li>
            </ul>
          </div>

          {/* ── Col 3: Services ── */}
          <div ref={(el) => (columnsRef.current[2] = el)} className="space-y-5">
            <h4
              className="text-sm font-bold tracking-widest text-slate-900 uppercase
 flex items-center gap-2"
            >
              <span className="inline-block w-4 h-0.5 bg-secondary-500 rounded-full" />
              Services
            </h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.id}>
                  <FooterLink to={`/services#${s.id}`}>{s.title}</FooterLink>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Col 4: Contact ── */}
          <div ref={(el) => (columnsRef.current[3] = el)} className="space-y-5">
            <h4
              className="text-sm font-bold tracking-widest text-slate-900 uppercase
 flex items-center gap-2"
            >
              <span className="inline-block w-4 h-0.5 bg-secondary-500 rounded-full" />
              Get In Touch
            </h4>

            <ul className="space-y-4">
              {/* Phone 1 */}
              <li>
                <a
                  href={`tel:${companyinfo?.phone}`}
                  className="flex items-start gap-3 group"
                >
                  <div
                    className="mt-0.5 w-7 h-7 rounded-lg bg-primary-50
 border border-primary-200 flex items-center justify-center
 flex-shrink-0 group-hover:bg-primary-100 transition-colors duration-300"
                  >
                    <Phone size={12} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                      Phone
                    </p>
                    <p
                      className="text-sm text-slate-700 group-hover:text-slate-900
 transition-colors duration-300"
                    >
                      {companyinfo?.phone}
                    </p>
                    <p
                      className="text-sm text-slate-600 group-hover:text-slate-700
 transition-colors duration-300"
                    >
                      {companyinfo?.phone2}
                    </p>
                  </div>
                </a>
              </li>

              {/* Email */}
              <li>
                <a
                  href={`mailto:${companyinfo?.email}`}
                  className="flex items-start gap-3 group"
                >
                  <div
                    className="mt-0.5 w-7 h-7 rounded-lg bg-primary-50
 border border-primary-200 flex items-center justify-center
 flex-shrink-0 group-hover:bg-primary-100 transition-colors duration-300"
                  >
                    <Mail size={12} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                      Email
                    </p>
                    <p
                      className="text-sm text-slate-700 break-all
 group-hover:text-slate-900 transition-colors duration-300"
                    >
                      {companyinfo?.email}
                    </p>
                  </div>
                </a>
              </li>

              {/* Address */}
              <li>
                <div className="flex items-start gap-3">
                  <div
                    className="mt-0.5 w-7 h-7 rounded-lg bg-primary-50
 border border-primary-200 flex items-center justify-center flex-shrink-0"
                  >
                    <MapPin size={12} className="text-primary-600" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-0.5">
                      Address
                    </p>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {companyinfo?.address}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      PoBox: {companyinfo?.poBox}
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── BOTTOM BAR ── */}
      <div
        ref={bottomBarRef}
        className="border-t border-slate-200 bg-slate-100"
      >
        <div
          className="container-fluid py-5 flex flex-col sm:flex-row
 items-center justify-between gap-4"
        >
          <p className="text-xs text-slate-500 text-center sm:text-left">
            {companyinfo?.copyright}
          </p>

          <div className="flex items-center gap-4">
            {/* Legal links */}
            <div className="hidden sm:flex items-center gap-4">
              {["Privacy Policy", "Terms of Use"].map((label) => (
                <Link
                  key={label}
                  to="#"
                  className="text-xs text-slate-500 hover:text-slate-700
 transition-colors duration-300"
                >
                  {label}
                </Link>
              ))}
            </div>

            <div className="w-px h-4 bg-slate-300 hidden sm:block" />
            <BackToTop />
          </div>
        </div>
      </div>

      {/* Bottom accent line */}
      <div
        className="h-[3px]"
        style={{
          background:
            "linear-gradient(90deg, #1d4ed8 0%, #3b82f6 40%, #f59e0b 70%, #d97706 100%)",
        }}
      />
    </footer>
  );
};

export default Footer;
